<script setup lang="ts">
import type { SessionResultChartItem } from '@/components/game/session-results/types.js';
import { cn, PSeparator } from '@pokerly/ui';
import { computed } from 'vue';
import { IconCards } from '@tabler/icons-vue';

interface SessionResultChartProps {
    data?: SessionResultChartItem[];
    recommendedValue?: number | string | null;
    mostVotedValue?: number | string | null;
}

interface NormalizedChartItem {
    key: string;
    label: string;
    votes: number;
    heightPercent: number;
    isRecommended: boolean;
    isMostVoted: boolean;
}

const props = withDefaults(defineProps<SessionResultChartProps>(), {
    data: () => [],
    recommendedValue: null,
    mostVotedValue: null,
});

function normalizeValue(value: number | string | null | undefined): string {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim();
}

function parseNumericValue(value: string): number | null {
    const normalized = value.trim();
    if (!/^[-+]?\d+(\.\d+)?$/.test(normalized)) {
        return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

function sortByLabel(a: SessionResultChartItem, b: SessionResultChartItem): number {
    const leftLabel = normalizeValue(a.value);
    const rightLabel = normalizeValue(b.value);

    const leftNumeric = parseNumericValue(leftLabel);
    const rightNumeric = parseNumericValue(rightLabel);

    if (leftNumeric !== null && rightNumeric !== null) {
        return leftNumeric - rightNumeric;
    }

    if (leftNumeric !== null) {
        return -1;
    }

    if (rightNumeric !== null) {
        return 1;
    }

    return leftLabel.localeCompare(rightLabel);
}

const MIN_BAR_PERCENT = 12;

const normalizedData = computed<NormalizedChartItem[]>(() => {
    const sortedData = [...props.data].sort(sortByLabel);
    const recommendedKey = normalizeValue(props.recommendedValue);
    const mostVotedKey = normalizeValue(props.mostVotedValue);

    const maxVotes = sortedData.reduce((max, entry) => Math.max(max, entry.votes), 0);
    const safeMax = maxVotes > 0 ? maxVotes : 1;
    const fallbackMostVotedKey = sortedData.find((entry) => entry.votes === maxVotes && maxVotes > 0)?.value ?? null;

    return sortedData.map((entry) => {
        const key = normalizeValue(entry.value);
        const isMostVoted = mostVotedKey ? key === mostVotedKey : key === normalizeValue(fallbackMostVotedKey);
        const isRecommended = recommendedKey.length > 0 && key === recommendedKey;

        const rawPercent = (entry.votes / safeMax) * 100;
        const heightPercent = entry.votes > 0 ? Math.max(rawPercent, MIN_BAR_PERCENT) : MIN_BAR_PERCENT;

        return {
            key,
            label: key,
            votes: entry.votes,
            heightPercent,
            isRecommended,
            isMostVoted,
        };
    });
});
</script>

<template>
    <div class="w-full overflow-x-auto">
        <div
            v-if="normalizedData.length > 0"
            class="grid"
            :style="{
                gridTemplateColumns: `72px repeat(${normalizedData.length}, minmax(36px, 1fr))`,
                minWidth: `${72 + normalizedData.length * 48}px`,
            }"
        >
            <!-- Left gutter: icon + "Votes" label -->
            <div
                class="flex flex-col items-center justify-end gap-[3.5px] paragraph-xs text-foreground-alt opacity-50 py-2"
            >
                <div class="px-2">
                    <IconCards class="size-4" />
                </div>
                <PSeparator />
                <span>Votes</span>
            </div>

            <!-- One column per data point -->
            <div v-for="item in normalizedData" :key="item.key" class="flex justify-center">
                <div
                    class="group/column flex flex-col items-center gap-4 min-w-12 py-2 rounded-lg cursor-default hover:bg-surface-ghost-hover"
                    :data-recommended="item.isRecommended"
                    :data-most-voted="item.isMostVoted"
                >
                    <!-- Bar container (fixed height, bar grows from bottom) -->
                    <div class="flex items-end justify-center h-20 w-full">
                        <div
                            :class="
                                cn(
                                    'bg-surface-accent-3',
                                    'rounded-full',
                                    'w-2.5',
                                    'transition-all',
                                    'group-data-[most-voted=true]/column:ring-2',
                                    'group-data-[most-voted=true]/column:ring-manaca-400',
                                    'group-data-[recommended=true]/column:bg-patilla-500',
                                )
                            "
                            :style="{ height: `${item.heightPercent}%` }"
                        />
                    </div>

                    <div class="flex flex-col gap-2 px-2 paragraph-xs text-foreground-alt text-center">
                        <!-- Card value -->
                        <span class="">{{ item.label }}</span>

                        <!-- Vote count -->
                        <span class="opacity-50">{{ item.votes }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
